import './FlyoutMap.css'
import PropTypes from 'prop-types';

import {FlyoutPlanContext} from "./flyout-plan-context.js";
import {useContext, useState, useEffect, useRef} from "react";
import {fetch} from "../assets/virtual-flyout/waypont-service.js";

import {Map, View} from 'ol';
import OSM from 'ol/source/OSM';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import {fromLonLat} from 'ol/proj';
import {ScaleLine} from 'ol/control';
import {Fill, Icon, Stroke, Style, Text} from "ol/style";
import {LineString} from "ol/geom";
import {useSelector} from "react-redux";

const ICONS = {
  'VOR': '/virtual-flyout/map-icons/navaid-vor.png',
  'DME': '/virtual-flyout/map-icons/navaid-dme.png',
  'NDB': '/virtual-flyout/map-icons/navaid-ndb.png',
  'TACAN': '/virtual-flyout/map-icons/navaid-tacan.png',
  'VORTAC': '/virtual-flyout/map-icons/navaid-vortac.png',
  'VORDME': '/virtual-flyout/map-icons/navaid-vordme.png',
  'AD': '/virtual-flyout/map-icons/airport.png',
  'WP': '/virtual-flyout/map-icons/waypoint.png'
};


function getTrackCoords(route) {
  const coords = [];
  for (const wp of route) {
    coords.push(fromLonLat([wp.long, wp.lat]));
  }
  return coords;
}

FlyoutMap.propTypes = {
  onSelectPlane: PropTypes.func
};

export default function FlyoutMap({onSelectPlane}) {
  const simState = useSelector(state => state.sim);
  const planesState = useSelector(state => state.planes);

  const {plan, allPlans, activeDay, selectedPlaneId} = useContext(FlyoutPlanContext);
  const [routes, setRoutes] = useState({});

  const mapSectionRef = useRef(null);
  const mapRef = useRef(null);
  const objectSource = useRef(new VectorSource({}));
  const trackSource = useRef(new VectorSource({}));
  const mapView = useRef(null);

  function extractRoute(plan, activeDay) {
    const route = [];
    let prevLegEnd = null;
    for (let day of plan.days) {
      prevLegEnd = null;
      for (let leg of day.legs) {
        if (leg.type === "flight") {
          const legStart = leg.segments[0].from;
          if (legStart && (!prevLegEnd || legStart.code !== prevLegEnd.code)) {
            if (day.name === activeDay || !activeDay) {
              route.push(legStart);
            }
            prevLegEnd = legStart;
          }
          for (let segment of leg.segments) {
            const wp = segment.to;
            if (wp) {
              if (day.name === activeDay || !activeDay) {
                route.push(wp);
              }
              prevLegEnd = wp;
            }
          }
        }
      }
    }
    return route;
  }

  // extract route from the plan
  useEffect(() => {
    if (plan) {
      const planeId = plan.plane?.reg;
      if (planeId) {
        setRoutes(prevRoutes => ({
          ...prevRoutes,
          [planeId]: extractRoute(plan, activeDay)
        }));
      }
    } else if (allPlans) {
      for (const plan of allPlans) {
        const planeId = plan.plane?.reg;
        if (planeId) {
          setRoutes(prevRoutes => ({
            ...prevRoutes,  
            [planeId]: extractRoute(plan, activeDay)
          }));
        }
      }
    }
  }, [plan, allPlans, activeDay]);

  useEffect(() => {
    const mapLayer = new TileLayer({source: new OSM()});
    const objectsLayer = new VectorLayer({
      source: objectSource.current,
      zIndex: 3
    });
    const trackLayer = new VectorLayer({
      source: trackSource.current,
      zIndex: 2
    });

    mapView.current = new View({
      center: fromLonLat([-1, 51.33]),
      zoom: 8
    });

    mapRef.current = new Map({
      target: mapSectionRef.current,
      controls: [
        new ScaleLine({units: 'nautical'}),
      ],
      layers: [mapLayer, objectsLayer, trackLayer],
      view: mapView.current
    });

    // Add click handler
    mapRef.current.on('click', (event) => {
      const feature = mapRef.current.forEachFeatureAtPixel(event.pixel, 
        (feature) => feature,
        {
          layerFilter: (layer) => layer === objectsLayer
        }
      );

      if (feature && feature.get('tag') === 'plane') {
        // Get plane data from feature
        const planeId = feature.get('planeId');
        onSelectPlane && onSelectPlane(planeId);
      } else {
        // Clicked elsewhere on map - deselect plane
        onSelectPlane && onSelectPlane(null);
      }
    });

    // Cleanup the map on component unmount
    return () => {
      mapRef.current.setTarget(null);
    };
  }, []);

  function createTrackFeature(track, isActive = false) {
    const trackFeature = new Feature({
      geometry: new LineString(track)
    });
    const color = isActive ? '#ff8000' : '#ffc29a';
    const zIndex = isActive ? 1 : 0;
    trackFeature.setStyle(new Style({
      stroke: new Stroke({
        color: color,
        width: 4,
        zIndex: zIndex
      })
    }));
    return trackFeature;
  }

  useEffect(() => {
    // Draw the waypoints and tracks for all routes
    objectSource.current.clear();
    trackSource.current.clear();

    let allWaypoints = [];
    
    Object.entries(routes).forEach(([planeId, route]) => {
      // Add waypoints
      for (const wp of route) {
        const objectFeature = new Feature({
          geometry: new Point(fromLonLat([wp.long, wp.lat])),
          tag: 'waypoint',
          type: wp.type,
          code: wp.code
        });
        objectFeature.setStyle(new Style({
          image: new Icon({
            src: ICONS[wp.type],
            anchor: [0.5, 0.5],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            scale: 0.08
          }),
          text: new Text({
            text: wp.code,
            font: 'bold 14px Calibri,sans-serif',
            offsetY: -20,
            fill: new Fill({color: 'black'}),
            stroke: new Stroke({color: 'white', width: 2}),
          })
        }));
        objectSource.current.addFeature(objectFeature);
        allWaypoints.push(wp);
      }

      // Draw the track
      const trackFeature = createTrackFeature(getTrackCoords(route), planeId === plan?.plane?.reg);
      trackSource.current.addFeature(trackFeature);
    });

    // Center the map on all routes if there are any waypoints
    if (allWaypoints.length > 1) {
      const extent = trackSource.current.getExtent();
      mapView.current.fit(extent, {padding: [50, 50, 50, 50]});
    }

  }, [routes]);

  if (simState.mode === "RUNNING") {
    // display the planes
    objectSource.current.getFeatures().forEach((feature) => {
      if (feature.get('tag') === 'plane') {
        objectSource.current.removeFeature(feature);
      }
    });
    for (const planeId of Object.keys(planesState)) {
      const plane = planesState[planeId];
      const planeFeature = new Feature({
        tag: 'plane',
        planeId: planeId,
        geometry: new Point(fromLonLat([plane.coordinates.long, plane.coordinates.lat]))
      });
      const planeColor = plane.distress ? "red" :
      selectedPlaneId === planeId ? "orange" : "yellow";
      planeFeature.setStyle(new Style({
        image: new Icon({
          src: `/virtual-flyout/map-icons/plane-${planeColor}.png`,
          anchor: [0.5, 0.5],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          scale: 0.03,
          rotation: plane.track * Math.PI / 180
        }),
        text: new Text({
          text: planeId,
          font: 'bold 10px Calibri,sans-serif',
          offsetY: 30,
          padding: [2,2,2,2],
          fill: new Fill({color: 'black'}),
          stroke: new Stroke({color: 'white', width: 2}),
          backgroundFill: new Fill({color: 'rgba(255, 255, 255, 0.6)'}),
        }),
        zIndex: 4
      }));
      objectSource.current.addFeature(planeFeature);
    }
  }

  // simulated track
  const simTrack = useSelector(state => state.planes[plan?.plane?.reg || selectedPlaneId]?.trackLog);
  useEffect(() => {
    const existingTrack = trackSource.current.getFeatures().find(feature => 
      feature.get('tag') === 'simTrack'
    );

    // If there's no track data, remove existing track if any
    if (!simTrack || simTrack.length === 0) {
      if (existingTrack) {
        trackSource.current.removeFeature(existingTrack);
      }
      return;
    }

    const simTrackCoords = simTrack.map((coord) => fromLonLat([coord.long, coord.lat]));
    
    // Update existing feature if it exists, otherwise create new one
    if (existingTrack) {
      existingTrack.getGeometry().setCoordinates(simTrackCoords);
    } else {
      const simTrackFeature = new Feature({
        tag: "simTrack",
        geometry: new LineString(simTrackCoords)
      });
      simTrackFeature.setStyle(new Style({
        stroke: new Stroke({
          color: '#0000FF',
          width: 4,
          zIndex: 1
        })
      }));
      trackSource.current.addFeature(simTrackFeature);
    }
  }, [simTrack]);

  return (
    <div className="flyout-plan-map">
      <div className="flyout-map" ref={mapSectionRef}>
      </div>
    </div>
  )
}